/**
 * Opening a run in /review that is NOT in the queue (TRA-1079).
 *
 * The review page keys on `?item=<trace id>` and resolves it against the
 * queue, which only holds flagged traces from the last 30 days. A row on
 * the Trajectories page has no flag most of the time, and Beatriz still
 * wants to land on it in review to add a verdict. So the link carries the
 * trace's R2 key too (`&key=…`), and when the queue does not know the id
 * the page builds this stand-in row from id + key: the transcript loads
 * off the key, the composer saves through the normal label upsert, and
 * the queue refetch replaces the stand-in with the real row.
 *
 * Nothing is written until the reviewer saves — clicking a link must not
 * plant an empty "unreviewed" flag that then counts against the nightly
 * review budget.
 */

import { parseTraceKey } from '@/components/traces/traceKey'
import type { getLabelByTraceApiLabelsByTraceTraceIdGetResponse } from '@/api/generated/labels/labels'
import type { LabelResponse, ReviewQueueItem } from '@/api/generated/model'

/** `created_by` of a stand-in row — never a real flag source. */
export const AD_HOC_SOURCE = 'ad-hoc'

export function isAdHocItem(item: ReviewQueueItem): boolean {
  return item.label_id === null && item.created_by === AD_HOC_SOURCE
}

/**
 * The stand-in row for the trace `traceKey` names, or null on a malformed
 * key — a broken link is said so, never reviewed as some other run.
 */
export function adHocQueueItem(traceKey: string, now: string): ReviewQueueItem | null {
  const parsed = parseTraceKey(traceKey)
  if (parsed === null) return null
  return {
    label_id: null,
    trace_id: parsed.traceId,
    review_status: 'unreviewed',
    approved: false,
    notes: '',
    created_by: AD_HOC_SOURCE,
    summary: '',
    tags: [],
    pr_url: null,
    trace_key: traceKey,
    // The api fills a queue row's `conversation_id` with the key SEGMENT
    // (percent-encoded), and the review page groups a run's flags by that
    // field (TRA-1088). A decoded id here would leave a stand-in alone on a
    // run whose other flags the queue does hold.
    conversation_id: parsed.conversationSegment,
    trace_date: parsed.date,
    created_at: now,
    updated_at: now,
  }
}

/**
 * The /review URL for a trace (TRA-1079). `key` lets the page open the run
 * even when it is not in the queue; the page ignores it otherwise.
 */
export function reviewUrl(traceId: string, traceKey: string): string {
  return `/review?item=${encodeURIComponent(traceId)}&key=${encodeURIComponent(traceKey)}`
}

/**
 * The label a stand-in run already carries, if any.
 *
 * A trace the queue does not hold can still have a label: a held flag
 * (parked over budget, TRA-1057) or a flag older than the queue's trace
 * window. The label upsert guards on that row's version and accepts a null
 * version only when no label exists (or it is tag-only), so a stand-in that
 * always sent null could never save a verdict or a dismissal on those runs.
 * And the composer replaces notes and verdict wholesale, so a stand-in that
 * did not carry the stored ones would save blanks over them. The page looks
 * the label up by trace id, folds it into the stand-in (`hydrateAdHocItem`)
 * and waits for the answer before it lets the reviewer save; a 404 means
 * there is nothing to guard.
 *
 * The answer must be fresh every time the stand-in appears: the same
 * label can be edited on the Trajectories page in between, and nothing
 * there invalidates this lookup. So the page refetches on every visit and
 * a cached answer under re-check is `refreshing`: good enough to show, not
 * to save on.
 */
export type ExistingLabel =
  | { status: 'pending' }
  | { status: 'refreshing'; label: LabelResponse | null }
  | { status: 'ready'; label: LabelResponse | null }
  | { status: 'failed' }

export function existingLabel(
  data: getLabelByTraceApiLabelsByTraceTraceIdGetResponse | undefined,
  query: { isFetching: boolean; isError: boolean; status: number | undefined },
): ExistingLabel {
  if (data !== undefined) {
    if (data.status !== 200) return { status: 'failed' }
    if (query.isFetching) return { status: 'refreshing', label: data.data }
    // A failed re-check leaves the cached answer in place. A 404 says the
    // label is gone since (flag removed elsewhere), so the cached copy is
    // not the answer any more; any other failure says nothing either way.
    if (query.isError) return absentOrFailed(query.status)
    return { status: 'ready', label: data.data }
  }
  if (query.isFetching || !query.isError) return { status: 'pending' }
  return absentOrFailed(query.status)
}

function absentOrFailed(errorStatus: number | undefined): ExistingLabel {
  return errorStatus === 404 ? { status: 'ready', label: null } : { status: 'failed' }
}

/**
 * The stand-in row with its stored label folded in.
 *
 * A tag-only label is not a flag: the row stays a stand-in and only learns
 * its tags. A flag makes the row a real one, the same shape the queue would
 * have built, so the header names the source, the composer re-opens the
 * stored feedback, and the save carries the label's version. `held` reads
 * as unreviewed: it is parked review work, and the composer seeds its
 * verdict pill from the flag's lean only for unreviewed rows.
 */
export function hydrateAdHocItem(standIn: ReviewQueueItem, label: LabelResponse): ReviewQueueItem {
  if (label.tag_only) return { ...standIn, tags: label.tags }
  return {
    ...standIn,
    label_id: label.id,
    review_status: label.review_status === 'held' ? 'unreviewed' : label.review_status,
    approved: label.approved,
    notes: label.notes,
    created_by: label.created_by,
    tags: label.tags,
    pr_url: label.pr_url,
    created_at: label.created_at,
    updated_at: label.updated_at,
  }
}
